import React, { useState, useEffect } from 'react';
import { api } from './api.js';
import styles from './Users.module.css';
import { hasPermission, PERMISSIONS, getUserData } from './permissions';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ userId: null, currentPassword: '', newPassword: '', confirmPassword: '' });
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userData = getUserData();
    setCurrentUser(userData);
    
    // Esperar a que ambas llamadas terminen
    Promise.all([
      api.getUsers(),
      api.getSectors()
    ]).then(([usersData, sectoresData]) => {
      console.log('✓ Users loaded:', usersData ? usersData.length : 0, 'users');
      console.log('✓ Sectors loaded:', sectoresData ? sectoresData.length : 0, 'sectors');
      console.log('Sectors data:', sectoresData);
      
      // Si es encargado, filtrar para mostrar solo sus datos
      let filteredUsers = Array.isArray(usersData) ? usersData : [];
      if (userData?.rol === 'encargado_de_sector') {
        filteredUsers = filteredUsers.filter(u => u.id === userData.id);
      }
      
      setUsers(filteredUsers);
      setSectors(Array.isArray(sectoresData) ? sectoresData : []);
      setLoading(false);
    }).catch(error => {
      console.error('Error al obtener datos:', error);
      setLoading(false);
    });
  }, []);

  const isAdmin = currentUser?.rol === 'administrador';
  const canEditUsers = hasPermission(currentUser?.rol, PERMISSIONS.EDIT_USER);
  const canCreateUsers = hasPermission(currentUser?.rol, PERMISSIONS.CREATE_USER);
  const canDeleteUsers = hasPermission(currentUser?.rol, PERMISSIONS.DELETE_USER);
  const canChangePassword = hasPermission(currentUser?.rol, PERMISSIONS.CHANGE_PASSWORD);

  const getSectorDisplay = (sectorId) => {
    if (!sectorId) return 'No asignado';
    
    const sector = sectors.find(s => {
      return String(s.id) === String(sectorId) || s.id === sectorId;
    });
    
    if (sector) {
      return `${sector.id_sector} - ${sector.nombre_sector}`;
    } else {
      console.warn(`Sector no encontrado para ID: ${sectorId}, Sectores disponibles:`, sectors);
      return `Sector ${sectorId}`;
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setEditingData({
      nombre: user.nombre,
      apellido: user.apellido,
      sector_id: user.sector_id,
      rol: user.rol
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingData({
      ...editingData,
      [name]: value
    });
  };

  const handleSaveEdit = async () => {
    try {
      await api.updateUser({
        id: editingId,
        nombre: editingData.nombre,
        apellido: editingData.apellido,
        sector_id: editingData.sector_id,
        rol: editingData.rol
      });
      setUsers(users.map(user => 
        user.id === editingId 
          ? { ...user, ...editingData } 
          : user
      ));
      setEditingId(null);
      alert('Usuario actualizado exitosamente');
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      alert('Error al actualizar usuario');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleOpenPasswordModal = (userId) => {
    setPasswordData({ userId, currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) {
      alert('Debe ingresar la contraseña actual');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await api.updateUser({
        id: passwordData.userId,
        contraseña: passwordData.newPassword,
        contraseña_actual: passwordData.currentPassword
      });
      alert('Contraseña actualizada exitosamente');
      setShowPasswordModal(false);
      setPasswordData({ userId: null, currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      alert('Error al cambiar contraseña: ' + (error.message || 'Contraseña actual incorrecta'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('¿Está seguro de eliminar este usuario?')) {
      try {
        await api.deleteUser(userId);
        setUsers(users.filter(user => user.id !== userId));
        alert('Usuario eliminado exitosamente');
      } catch (error) {
        console.error('Error al eliminar usuario:', error);
        alert('Error al eliminar usuario');
      }
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Cargando usuarios y sectores...</div>;
  }

  return (
    <div className={styles.usersContainer}>
      <h2>Gestión de Usuarios</h2>
      <div className={styles.tableContainer}>
        <table className={styles.usersTable}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Apellido</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Sector</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                {editingId === user.id ? (
                  <>
                    <td>
                      <input
                        type="text"
                        name="nombre"
                        value={editingData.nombre}
                        onChange={handleInputChange}
                        className={styles.editInput}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="apellido"
                        value={editingData.apellido}
                        onChange={handleInputChange}
                        className={styles.editInput}
                      />
                    </td>
                    <td>{user.correo || 'N/A'}</td>
                    <td>
                      <select
                        name="rol"
                        value={editingData.rol || 'administrador'}
                        onChange={handleInputChange}
                        className={styles.roleSelect}
                      >
                        <option value="administrador">Administrador</option>
                        <option value="encargado_de_sector">Encargado de Sector</option>
                      </select>
                    </td>
                    <td>
                      <select
                        name="sector_id"
                        value={editingData.sector_id || ''}
                        onChange={handleInputChange}
                        className={styles.sectorSelect}
                      >
                        <option value="">Sin asignar</option>
                        {sectors.map(sector => (
                          <option key={sector.id} value={sector.id}>
                            {sector.nombre_sector}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button 
                        className={styles.saveButton}
                        onClick={handleSaveEdit}
                      >
                        Guardar
                      </button>
                      <button 
                        className={styles.cancelButton}
                        onClick={handleCancelEdit}
                      >
                        Cancelar
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{user.nombre || 'N/A'}</td>
                    <td>{user.apellido || 'N/A'}</td>
                    <td>{user.correo || 'N/A'}</td>
                    <td>{user.rol || 'N/A'}</td>
                    <td>{getSectorDisplay(user.sector_id)}</td>
                    <td>
                      {canEditUsers && (
                        <button 
                          className={styles.editButton}
                          onClick={() => handleEdit(user)}
                        >
                          Editar
                        </button>
                      )}
                      {canChangePassword && (
                        <button 
                          className={styles.passwordButton}
                          onClick={() => handleOpenPasswordModal(user.id)}
                        >
                          Contraseña
                        </button>
                      )}
                      {canDeleteUsers && (
                        <button 
                          className={styles.deleteButton}
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPasswordModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Cambiar Contraseña</h3>
            <div className={styles.formGroup}>
              <label>Contraseña Actual:</label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                className={styles.passwordInput}
                placeholder="Ingrese contraseña actual"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Nueva Contraseña:</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                className={styles.passwordInput}
                placeholder="Ingrese nueva contraseña"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Confirmar Contraseña:</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                className={styles.passwordInput}
                placeholder="Confirme la contraseña"
              />
            </div>
            <div className={styles.modalButtons}>
              <button 
                className={styles.saveButton}
                onClick={handleChangePassword}
              >
                Cambiar Contraseña
              </button>
              <button 
                className={styles.cancelButton}
                onClick={() => setShowPasswordModal(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
